import { useEffect, useRef, useState } from 'react';
import { ChatBroadcast } from 'shared';
import { Panel } from '../../../ui/Panel';
import { Button } from '../../../ui/Button';
import { Input } from '../../../ui/Input';
import { Text } from '../../../ui/Text';
import { token, zVar } from '../../../ui/theme';
import { useGameWebSocket } from '../useGameWebSocket';

/*
 * ChatWindow — lightweight, ephemeral in-game chat. A fixed overlay pinned to
 * the bottom-right corner (rendered outside <Stage> so its text/input stay
 * crisp and unscaled), collapsible to a compact strip with an unread indicator.
 *
 * It shares the game socket via useGameWebSocket (share: true): it listens for
 * chat frames and ignores the game state frames and bare-string signals the
 * board uses. Messages are not persisted — history lives only for the life of
 * this mounted component.
 */

const isChatBroadcast = (value: unknown): value is ChatBroadcast =>
  !!value &&
  typeof value === 'object' &&
  (value as { type?: unknown }).type === 'chat' &&
  typeof (value as { sender?: unknown }).sender === 'string' &&
  typeof (value as { text?: unknown }).text === 'string';

export const ChatWindow = () => {
  const { lastMessage, sendJsonMessage } = useGameWebSocket();

  const [messages, setMessages] = useState<ChatBroadcast[]>([]);
  const [input, setInput] = useState('');
  const [collapsed, setCollapsed] = useState(true);
  const [unread, setUnread] = useState(0);

  const listRef = useRef<HTMLDivElement>(null);

  // Append incoming chat frames; ignore the bare-string board signals, which
  // are not valid JSON and simply fall through the try/catch.
  useEffect(() => {
    if (!lastMessage?.data) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(lastMessage.data);
    } catch {
      return;
    }
    if (!isChatBroadcast(parsed)) return;
    const message = parsed;
    setMessages((prev) => [...prev, message]);
    setCollapsed((isCollapsed) => {
      if (isCollapsed) setUnread((n) => n + 1);
      return isCollapsed;
    });
  }, [lastMessage]);

  // Keep the newest message in view.
  useEffect(() => {
    if (collapsed) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, collapsed]);

  const send = () => {
    const text = input.trim();
    if (!text) return;
    sendJsonMessage({ type: 'chat', text });
    setInput('');
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send();
  };

  const expand = () => {
    setCollapsed(false);
    setUnread(0);
  };

  return (
    <Panel
      variant="hud"
      padding={0}
      style={{
        position: 'fixed',
        left: 16,
        bottom: 16,
        width: 280,
        zIndex: zVar('z-hud'),
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header / collapse toggle */}
      <button
        type="button"
        className="ui-bare-btn"
        onClick={() => (collapsed ? expand() : setCollapsed(true))}
        style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-2)',
          padding: 'var(--space-2) var(--space-3)',
          borderBottom: collapsed ? 'none' : `1px solid ${token('color-border')}`,
        }}
      >
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}
        >
          <Text variant="label">💬 Chat</Text>
          {collapsed && unread > 0 && (
            <span
              style={{
                minWidth: 18,
                height: 18,
                padding: '0 5px',
                borderRadius: 'var(--radius-pill)',
                background: token('color-gold'),
                color: token('color-on-gold'),
                fontSize: token('font-size-xs'),
                fontWeight: 700,
                lineHeight: '18px',
                textAlign: 'center',
              }}
            >
              {unread}
            </span>
          )}
        </span>
        <Text variant="muted" aria-hidden>
          {collapsed ? '▲' : '▼'}
        </Text>
      </button>

      {!collapsed && (
        <>
          {/* Scrollable message list */}
          <div
            ref={listRef}
            style={{
              maxHeight: 220,
              minHeight: 80,
              overflowY: 'auto',
              padding: 'var(--space-3)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
            }}
          >
            {messages.length === 0 ? (
              <Text variant="muted">No messages yet.</Text>
            ) : (
              messages.map((m, i) => (
                <div key={i} style={{ fontSize: token('font-size-sm'), lineHeight: 1.35 }}>
                  <Text variant="label" style={{ marginRight: 'var(--space-2)' }}>
                    {m.sender}
                  </Text>
                  <span
                    style={{
                      color: token('color-text'),
                      wordBreak: 'break-word',
                    }}
                  >
                    {m.text}
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Composer */}
          <form
            onSubmit={onSubmit}
            style={{
              display: 'flex',
              gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-3) var(--space-3)',
            }}
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a message…"
              maxLength={300}
              style={{ flex: 1, minWidth: 0 }}
            />
            <Button type="submit" variant="primary" size="sm">
              Send
            </Button>
          </form>
        </>
      )}
    </Panel>
  );
};

export default ChatWindow;
